import { toZonedTime, formatInTimeZone } from 'date-fns-tz';
import { format, parse, parseISO, isValid } from 'date-fns';
import { fr } from 'date-fns/locale';

export const formatDate = (dateString) => {
  if (!dateString) return '';
  return format(new Date(dateString), 'dd/MM/yyyy', { locale: fr });
};

// Sérialise une Date en paramètre d'URL `yyyy-MM-dd` en heure LOCALE.
// Volontairement local (pas toISOString/UTC) pour round-tripper sans dérive
// d'un jour avec safeParseDate, qui parse `yyyy-MM-dd` à minuit local.
export const toDateParam = (date) => {
  if (!(date instanceof Date) || !isValid(date)) return null;
  return format(date, 'yyyy-MM-dd');
};

// Fonction utilitaire pour parser les dates de manière sécurisée
export const safeParseDate = (dateValue) => {
  if (!dateValue) return null;

  try {
    // Si c'est déjà un objet Date valide, le retourner
    if (dateValue instanceof Date) {
      return isValid(dateValue) ? dateValue : null;
    }

    const dateString = String(dateValue).trim();

    // Essayer différents formats de parsing
    let parsedDate = null;

    // Format spécifique problématique: "2024-12-11 19:32:06.470 +0100"
    const customFormatMatch = dateString.match(/^(\d{4}-\d{2}-\d{2})\s(\d{2}:\d{2}:\d{2})\.(\d{3})\s([+-]\d{4})$/);

    if (customFormatMatch) {
      const [, datePart, timePart, milliseconds, timezone] = customFormatMatch;
      // Convertir en format ISO standard
      const isoString = `${datePart}T${timePart}.${milliseconds}${timezone.slice(0, 3)}:${timezone.slice(3)}`;
      parsedDate = parseISO(isoString);

      if (isValid(parsedDate)) {
        return parsedDate;
      }
    }

    // Essayer le parsing ISO standard
    try {
      parsedDate = parseISO(dateString);

      if (isValid(parsedDate)) {
        return parsedDate;
      }
    } catch (e) {
      // Continuer avec les autres méthodes
    }

    // Essayer avec new Date() comme fallback
    try {
      parsedDate = new Date(dateString);

      if (isValid(parsedDate)) {
        return parsedDate;
      }
    } catch (e) {
      // Continuer
    }

    // Essayer des formats spécifiques avec date-fns parse
    const formats = [
      'yyyy-MM-dd HH:mm:ss.SSS xxx', // Format avec timezone
      'yyyy-MM-dd HH:mm:ss.SSS',     // Format sans timezone
      'yyyy-MM-dd HH:mm:ss',         // Format sans millisecondes
      'yyyy-MM-dd',                  // Format date seule
      'dd/MM/yyyy',                  // Format français
      'MM/dd/yyyy',                  // Format américain
    ];

    for (const formatString of formats) {
      try {
        parsedDate = parse(dateString, formatString, new Date());

        if (isValid(parsedDate)) {
          return parsedDate;
        }
      } catch (e) {
        // Continuer avec le format suivant
      }
    }

    console.warn('Date invalide après tous les essais de parsing:', dateValue);
    return null;

  } catch (error) {
    console.warn('Erreur de parsing de date:', dateValue, error);
    return null;
  }
};

// Fonction pour convertir une date UTC vers la timezone locale
export const convertUTCToLocal = (utcDateValue) => {
  const utcDate = safeParseDate(utcDateValue);
  if (!utcDate) return null;

  // Si la date se termine par 'Z', c'est déjà en UTC
  // Sinon, on assume que c'est en UTC et on la traite comme telle
  return toZonedTime(utcDate, 'Europe/Paris');
};

// Fonction pour formater une date UTC en string locale de manière sécurisée
export const safeFormatUTCDate = (utcDateValue, locale = 'fr-FR', options = {}) => {
  const localDate = convertUTCToLocal(utcDateValue);
  return localDate ? localDate.toLocaleDateString(locale, options) : 'Date invalide';
};

// Fonction pour formater une date UTC avec heure en string locale
export const safeFormatUTCDateTime = (utcDateValue, locale = 'fr-FR', options = {}) => {
  const localDate = convertUTCToLocal(utcDateValue);

  if (!localDate) return 'Date invalide';

  const defaultOptions = {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    ...options
  };

  const result = localDate.toLocaleDateString(locale, defaultOptions);

  return result;
};

// Fonction pour formater une date en string locale de manière sécurisée (legacy)
export const safeFormatDate = (dateValue, locale = 'fr-FR', options = {}) => {
  const date = safeParseDate(dateValue);
  return date ? date.toLocaleDateString(locale, options) : 'Date invalide';
};